import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { Droppable, type DroppableProvided } from 'react-beautiful-dnd';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import DisabledButtonWithTooltip from '@atlassian/jira-disabled-button-with-tooltip/src/index.tsx';
import { type IntlShape, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import {
	CHILD_OPTION_DRAGGABLE_TYPE,
	PARENT_OPTION_DRAGGABLE_TYPE,
	toDroppableId,
} from '@atlassian/jira-project-settings-apps-common/src/options/index.tsx';
import type { OptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	AssociatedField,
	FieldOptions as FieldOptionsType,
	OptionType,
} from '../../../../../types/layout-items.tsx';
import type { Limits, ValidationError } from '../../../../../types/validation.tsx';
import { POSITION_FIELD_OPTIONS } from '../../../../../utils/validation/constants.tsx';
import {
	isDefaultOption,
	isOptionsPerFieldLimitReached,
	refineDefaultValue,
} from '../../field-options/view.tsx';
import { type DefaultValueControlComponentProps, Option } from '../option/view.tsx';

import { canAddNewOptions } from '../util.tsx';
import messages from './messages.tsx';

export type GroupedOptions = {
	[key: string]: FieldOptionsType;
};

export type ParentId = OptionUuid | 'root';

export type Props = {
	readonly label: MessageDescriptor;
	readonly field: AssociatedField;
	readonly fieldErrors: ReadonlyArray<ValidationError>;
	readonly DefaultValueControlLeft?: ComponentType<DefaultValueControlComponentProps>;
	readonly DefaultValueControlRight?: ComponentType<DefaultValueControlComponentProps>;
	parentId: ParentId;
	/**
	 * The most recently added option is going to be autoFocused
	 */
	lastAddedOptimisticId: null | string;
	optionsByParent: GroupedOptions;
	onUpdate: (arg1: AssociatedField) => void;
	onEditOption: (option: OptionType, newValue: string) => void;
	onRemoveOption: (option: OptionType) => void;
	onAddOption: (parentId: ParentId) => void;
	readonly limits: Limits;
	readonly tooltipDelay?: number;
};
const getTranslatedErrorFor = (
	option: OptionType,
	fieldErrors: ReadonlyArray<ValidationError>,
	intl: IntlShape,
): string | undefined => {
	const error: ValidationError | undefined = fieldErrors.find(
		(err) =>
			err.position === POSITION_FIELD_OPTIONS &&
			err.positionId != null &&
			err.positionId.optionOptimisticId === option.optimisticId,
	);
	if (!error) {
		return undefined;
	}
	return intl.formatMessage(error.messageDescriptor, error.values);
};

const getAddButtonTooltip = (
	field: AssociatedField,
	limits: Limits,
	intl: IntlShape,
): string | null => {
	if (!field.operations.canModifyOptions) {
		return intl.formatMessage(messages.addButtonDisabledNoModify);
	}
	if (isOptionsPerFieldLimitReached(field, limits)) {
		return intl.formatMessage(messages.addButtonDisabledMaxOptions, {
			maxOptions: limits.optionsPerField,
		});
	}
	return null;
};

export const CascadingFieldOptionsList = (props: Props) => {
	const onAddOption = () => {
		props.onAddOption(props.parentId);
	};

	const intl: IntlShape = useIntl();

	const {
		field,
		fieldErrors,
		optionsByParent,
		label,
		DefaultValueControlLeft,
		DefaultValueControlRight,
		parentId,
		onRemoveOption,
		onEditOption,
		lastAddedOptimisticId,
		onUpdate,
		limits,
		tooltipDelay,
	} = props;
	const options = optionsByParent[parentId] || [];
	const isTopLevel = parentId === 'root';
	const isAddDisabled = !canAddNewOptions(field, limits);
	// There must be at least 1 option at the top level
	const canDeleteOptions = !isTopLevel || options.length > 1;

	const preselectedOptions: ReadonlyArray<OptionType> = refineDefaultValue(field.defaultValue);

	// Droppable only rerenders when drag-and-drop information changes.
	// Options are not drag and drop information, they come from us,
	// so we need to use a different function every time DropdownOptions renders,
	// to trigger a prop change (therefore rerender) of Droppable.
	return (
		<Box xcss={dropdownOptionsStyledStyles}>
			{isTopLevel ? (
				<Heading size="xxsmall" as="div">
					{intl.formatMessage(label)}
				</Heading>
			) : null}
			<Droppable
				droppableId={toDroppableId(field.key, parentId)}
				type={isTopLevel ? PARENT_OPTION_DRAGGABLE_TYPE : CHILD_OPTION_DRAGGABLE_TYPE}
			>
				{({ innerRef, droppableProps, placeholder }: DroppableProvided) => (
					<>
						<List ref={innerRef} {...droppableProps}>
							{options.map((option, index) => (
								<Option
									key={option.optimisticId}
									placeholder={intl.formatMessage(messages.optionPlaceholder, {
										index: index + 1,
									})}
									option={option}
									field={field}
									autoFocus={option.optimisticId === lastAddedOptimisticId}
									onRemove={onRemoveOption}
									onEnterKey={onAddOption}
									onEdit={onEditOption}
									onUpdate={onUpdate}
									isDeletable={canDeleteOptions}
									isDefault={isDefaultOption(preselectedOptions)(option)}
									DefaultValueControlLeft={DefaultValueControlLeft}
									DefaultValueControlRight={DefaultValueControlRight}
									translatedError={getTranslatedErrorFor(option, fieldErrors, intl)}
									index={index}
									tooltipDelay={tooltipDelay}
								>
									{isTopLevel && option.externalUuid ? (
										<CascadingFieldOptionsList {...props} parentId={option.externalUuid} />
									) : null}
								</Option>
							))}
							{/* Render the droppable placeholder so content doesn't shift when dragging/dropping elements */}
							{placeholder}

							{!fg('a11y-fix-eng-health-cascading-field-options-list') && (
								<Tooltip content={getAddButtonTooltip(field, limits, intl)} delay={tooltipDelay}>
									<DisabledButtonWithTooltip isDisabled={isAddDisabled}>
										<Button
											isDisabled={isAddDisabled}
											appearance="subtle"
											// label is not needed on this icon because there is button text
											iconBefore={<EditorAddIcon label="" />}
											onClick={onAddOption}
										>
											{intl.formatMessage(
												isTopLevel ? messages.addNewParentOption : messages.addNewChildOption,
											)}
										</Button>
									</DisabledButtonWithTooltip>
								</Tooltip>
							)}
						</List>

						{fg('a11y-fix-eng-health-cascading-field-options-list') && (
							<Tooltip content={getAddButtonTooltip(field, limits, intl)} delay={tooltipDelay}>
								<DisabledButtonWithTooltip isDisabled={isAddDisabled}>
									<Button
										isDisabled={isAddDisabled}
										appearance="subtle"
										// label is not needed on this icon because there is button text
										iconBefore={<EditorAddIcon label="" />}
										onClick={onAddOption}
									>
										{intl.formatMessage(
											isTopLevel ? messages.addNewParentOption : messages.addNewChildOption,
										)}
									</Button>
								</DisabledButtonWithTooltip>
							</Tooltip>
						)}
					</>
				)}
			</Droppable>
		</Box>
	);
};

const dropdownOptionsStyledStyles = xcss({
	paddingRight: 'space.500',
	paddingLeft: 'space.500',
	marginBottom: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ol({
	listStyleType: 'none',
	paddingLeft: token('space.0'),
	marginTop: token('space.050'),
	marginRight: token('space.0'),
	marginBottom: token('space.0'),
	marginLeft: token('space.0'),
});
